import Add16 from '@carbon/icons-react/es/add/16';
import { Col, Row } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { MessageType, UserMessage } from 'api/conversations';
import { Button } from 'components/atoms/button';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { ActionBar } from 'components/molecules/action-bar';
import { ConversationContext, createCommentAction } from 'data/conversation';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';

const EditorWrapper = styled.div`
  --rte-background: var(--private-02);
  background: var(--private-02);

  &:hover {
    --rte-background: var(--private-01);

    background: var(--private-01);
  }
`;

type CommentBox = {
  onClose: () => void;
  context: ConversationContext;
};

export const CommentBox: FC<CommentBox> = ({ onClose, context }) => {
  const currentUser = useUser();
  const dispatch = useDispatch();
  const [rte, setRte] = useState<RichTextEditor | null>(null);
  const [html, setHtml] = useState('');
  const setRteContent = useCallback((_html) => setHtml(_html), []);

  const placeholderText = useTranslation('task.add_jira_comment.placeholder');
  const submitText = useTranslation('task.add_jira_comment.sumbit');
  const cancelText = useTranslation('cancel');

  const getEditorProps = () => {
    return {
      ref: setRte,
      placeholder: placeholderText,
      data: html,
      onChange: setRteContent,
      disallowMentions: true,
    };
  };

  const onSubmit = () => {
    const newComment: Partial<UserMessage> = {
      messageType: MessageType.UserMessage,
      content: html,
      private: true,
      commentMeta: {
        syncedWithExternalIntegration: true,
      },
      user: currentUser.user,
    };

    dispatch(createCommentAction({ comment: newComment, context }));
    setHtml('');
    onClose();
  };

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <EditorWrapper>
          <RichTextEditor {...getEditorProps()} />
          <ActionBar private>
            <ActionBar.Secondary>
              <RichTextEditor.EmojiPicker
                enabled
                onSelect={(emoji) => rte?.addEmoji(emoji)}
              />
            </ActionBar.Secondary>
          </ActionBar>
        </EditorWrapper>
      </Col>
      <Col span={24}>
        <Button type="primary" icon={<Add16 />} onClick={onSubmit}>
          {submitText}
        </Button>
        <Button type="text" onClick={onClose}>
          {cancelText}
        </Button>
      </Col>
    </Row>
  );
};
